import React, { useState } from 'react';
import { Globe, MapPin, X, Check } from 'lucide-react';
import { t } from '../services/i18n';

interface LanguageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (analysisLanguage: string, uiLanguage: string, aiAssistantLanguage: string, country: string) => void;
  currentAnalysisLanguage?: string;
  currentUiLanguage?: string;
  currentAiAssistantLanguage?: string;
  currentCountry?: string;
  showOnLanding?: boolean; // If true, show on landing page before login
}

// Supported languages for UI (website interface)
const UI_LANGUAGES = [
  { value: 'English', label: 'English', code: 'en' },
  { value: 'French', label: 'Français', code: 'fr' },
  { value: 'Arabic', label: 'العربية', code: 'ar' },
  { value: 'Darija', label: 'Darija', code: 'darija' },
  { value: 'German', label: 'Deutsch', code: 'de' },
  { value: 'Spanish', label: 'Español', code: 'es' },
];

// Supported languages for analysis results (can include more)
const ANALYSIS_LANGUAGES = [
  { value: 'English', label: 'English' },
  { value: 'French', label: 'Français' },
  { value: 'Arabic', label: 'العربية' },
  { value: 'Darija/French', label: 'Darija/Français' },
  { value: 'German', label: 'Deutsch' },
  { value: 'Spanish', label: 'Español' },
];

// Supported languages for AI Assistant (same as analysis, but separate selection)
const AI_ASSISTANT_LANGUAGES = [
  { value: 'Darija/French', label: 'Darija/Français (Recommended)' },
  { value: 'English', label: 'English' },
  { value: 'French', label: 'Français' },
  { value: 'Arabic', label: 'العربية' },
  { value: 'German', label: 'Deutsch' },
  { value: 'Spanish', label: 'Español' },
];

const COUNTRIES = [
  { value: 'Morocco', label: 'Morocco 🇲🇦' },
  { value: 'United States', label: 'United States 🇺🇸' },
  { value: 'United Kingdom', label: 'United Kingdom 🇬🇧' },
  { value: 'France', label: 'France 🇫🇷' },
  { value: 'Canada', label: 'Canada 🇨🇦' },
  { value: 'Germany', label: 'Germany 🇩🇪' },
  { value: 'Spain', label: 'Spain 🇪🇸' },
  { value: 'Italy', label: 'Italy 🇮🇹' },
  { value: 'Netherlands', label: 'Netherlands 🇳🇱' },
  { value: 'Belgium', label: 'Belgium 🇧🇪' },
  { value: 'Switzerland', label: 'Switzerland 🇨🇭' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia 🇸🇦' },
  { value: 'UAE', label: 'UAE 🇦🇪' },
  { value: 'Egypt', label: 'Egypt 🇪🇬' },
  { value: 'Algeria', label: 'Algeria 🇩🇿' },
  { value: 'Tunisia', label: 'Tunisia 🇹🇳' },
  { value: 'Brazil', label: 'Brazil 🇧🇷' },
  { value: 'Mexico', label: 'Mexico 🇲🇽' },
  { value: 'Argentina', label: 'Argentina 🇦🇷' },
  { value: 'Australia', label: 'Australia 🇦🇺' },
  { value: 'Japan', label: 'Japan 🇯🇵' },
  { value: 'South Korea', label: 'South Korea 🇰🇷' },
  { value: 'China', label: 'China 🇨🇳' },
  { value: 'India', label: 'India 🇮🇳' },
];

export const LanguageSelectionModal: React.FC<LanguageSelectionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentAnalysisLanguage = 'English',
  currentUiLanguage = 'English',
  currentAiAssistantLanguage = 'Darija/French',
  currentCountry = 'United States',
  showOnLanding = false
}) => {
  const [selectedAnalysisLanguage, setSelectedAnalysisLanguage] = useState(currentAnalysisLanguage);
  const [selectedUiLanguage, setSelectedUiLanguage] = useState(currentUiLanguage);
  const [selectedAiAssistantLanguage, setSelectedAiAssistantLanguage] = useState(currentAiAssistantLanguage);
  const [selectedCountry, setSelectedCountry] = useState(currentCountry);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(selectedAnalysisLanguage, selectedUiLanguage, selectedAiAssistantLanguage, selectedCountry);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Globe className="text-indigo-400" size={24} />
              {showOnLanding ? (t('language.welcomeTitle') || 'Welcome! Choose Your Preferences') : (t('language.selectTitle') || 'Select Your Preferences')}
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              {t('language.selectDescription') || 'Choose your language for analysis results and website interface, plus your target market'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-800 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Analysis Results Language */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Globe size={16} className="text-indigo-400" />
              {t('language.analysisLanguage') || 'Analysis Results Language'}
              <span className="text-xs text-slate-400 ml-2">({t('language.analysisLanguageDesc') || 'Language for SEO audit reports - can be Darija'})</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {ANALYSIS_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => setSelectedAnalysisLanguage(lang.value)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    selectedAnalysisLanguage === lang.value
                      ? 'border-indigo-500 bg-indigo-500/20 text-white'
                      : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{lang.label}</span>
                    {selectedAnalysisLanguage === lang.value && (
                      <Check size={16} className="text-indigo-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Website UI Language */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Globe size={16} className="text-blue-400" />
              {t('language.uiLanguage') || 'Website Interface Language'}
              <span className="text-xs text-slate-400 ml-2">({t('language.uiLanguageDesc') || 'Language for buttons, menus, navigation - FR or EN recommended'})</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {UI_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => setSelectedUiLanguage(lang.value)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    selectedUiLanguage === lang.value
                      ? 'border-blue-500 bg-blue-500/20 text-white'
                      : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{lang.label}</span>
                    {selectedUiLanguage === lang.value && (
                      <Check size={16} className="text-blue-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* AI Assistant Language */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Globe size={16} className="text-purple-400" />
              {t('language.aiAssistantLanguage') || 'AI Assistant Language'}
              <span className="text-xs text-slate-400 ml-2">({t('language.aiAssistantLanguageDesc') || 'Language for AI chat, meta tags, content optimizer - Darija recommended'})</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {AI_ASSISTANT_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => setSelectedAiAssistantLanguage(lang.value)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    selectedAiAssistantLanguage === lang.value
                      ? 'border-purple-500 bg-purple-500/20 text-white'
                      : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{lang.label}</span>
                    {selectedAiAssistantLanguage === lang.value && (
                      <Check size={16} className="text-purple-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Country Selection */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <MapPin size={16} className="text-emerald-400" />
              {t('language.targetCountry') || 'Target Market (Country)'}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
              {COUNTRIES.map((country) => (
                <button
                  key={country.value}
                  onClick={() => setSelectedCountry(country.value)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    selectedCountry === country.value
                      ? 'border-emerald-500 bg-emerald-500/20 text-white'
                      : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{country.label}</span>
                    {selectedCountry === country.value && (
                      <Check size={16} className="text-emerald-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8 pt-6 border-t border-slate-700">
          <button
            onClick={handleSave}
            className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <Check size={18} />
            {t('language.savePreferences') || 'Save Preferences'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium rounded-xl transition-colors"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};
